import React, { useEffect, useState } from 'react';
import OffcanvasPagosDuplicados from '../modals/OffcanvasPagosDuplicados';
import { Card, InputGroup, FormControl, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

const PagosReferenciasDuplicadas = ({ filters, setID_Pago, setShowModalConciliar, refreshTrigger }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [CargandoGetRefDuplicadas, setCargandoGetRefDuplicadas] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [referenciasDuplicadas, setReferenciasDuplicadas] = useState([]);

    // Estado del Offcanvas
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Paginación
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 5;
    const [TotalRegistros, setTotalRegistros] = useState(0);

    // Estado para la referencia activa (seleccionada)
    const [activeRef, setActiveRef] = useState(null);

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.month && filters.year) {
                ObtenerAlertaReferencias();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [
        filters.month,
        filters.year,
        filters.idCatCuentaBancaria,
        filters.idCatMedioPago,
        filters.idVendedor,
        filters.filterValidados,
        filters.filterStatus,
        pageNumber,
        pageSize,
        searchText,
        refreshTrigger
    ]);

    const ObtenerAlertaReferencias = async () => {
        if (!referenciasDuplicadas.length && !searchText && pageNumber === 1) {
            setCargandoGetRefDuplicadas(true);
        } else {
            setIsFetchingMore(true);
        }

        const response = await fetch(`/api/AdministracionPagos/ObtenerReferenciasDuplicadasList?pageNumber=${pageNumber}&pageSize=${pageSize}&searchText=${encodeURIComponent(searchText)}`, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + token
            }
        });

        if (response.ok) {
            const responseType = await response.json();
            if (responseType.iResponseType === 1) {
                const responseData = JSON.parse(responseType.detalles);
                const data = responseData.Data || [];
                const total = responseData.TotalRegistros || 0;

                setReferenciasDuplicadas(data);
                if (pageNumber === 1) {
                    setTotalRegistros(total);
                }

                if (data.length === 0) {
                    setActiveRef(null);
                }
            } else {
                showErrorToast(responseType.vMessage);
            }
        }
        setCargandoGetRefDuplicadas(false);
        setIsFetchingMore(false);
        setIsSearching(false);
    };

    // Calcular paginación con total de base de datos
    const totalPages = Math.ceil(TotalRegistros / pageSize);

    if (CargandoGetRefDuplicadas) {
        return <Card className="mb-3"><Card.Body><Skeleton height={50} count={2} /></Card.Body></Card>;
    }

    if (referenciasDuplicadas.length === 0 && !searchText && !isSearching) {
        return null; // Ocultar si no hay duplicadas y no se está buscando
    }

    return (
        <Card className="mb-3 shadow-sm border-200">
            <div className="d-flex flex-wrap align-items-center justify-content-between p-3 border-bottom border-200 gap-3">
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0 fw-bold text-uppercase text-dark">Referencias Repetidas</h6>
                    <span className="badge rounded-pill fw-semi-bold" style={{ backgroundColor: '#fdf2f2', color: '#e63757' }}>
                        {TotalRegistros} Detectadas
                    </span>
                </div>
                <div style={{ width: '100%', maxWidth: '350px' }}>
                    <InputGroup size="sm">
                        <InputGroup.Text className="bg-transparent border-end-0 text-500">
                            <FontAwesomeIcon icon="search" />
                        </InputGroup.Text>
                        <FormControl
                            placeholder="Buscar referencia..."
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setPageNumber(1);
                                setIsSearching(true);
                            }}
                            className="border-start-0 ps-0 shadow-none"
                        />
                    </InputGroup>
                </div>
            </div>

            <div className="p-3">
                {isSearching ? (
                    <div className="d-flex justify-content-center align-items-center py-4">
                        <Spinner animation="border" variant="danger" />
                    </div>
                ) : referenciasDuplicadas.length === 0 && searchText ? (
                    <div className="text-center py-4 text-500 fs--1">
                        No se encontraron referencias para "{searchText}"
                    </div>
                ) : (
                    <div className="d-flex align-items-center gap-3">
                        <style>
                            {`
                                .elegant-scrollbar::-webkit-scrollbar {
                                    height: 8px;
                                }
                                .elegant-scrollbar::-webkit-scrollbar-track {
                                    background: #f8f9fa;
                                    border-radius: 10px;
                                }
                                .elegant-scrollbar::-webkit-scrollbar-thumb {
                                    background: #adb5bd;
                                    border-radius: 10px;
                                }
                                .elegant-scrollbar::-webkit-scrollbar-thumb:hover {
                                    background: #6c757d;
                                }
                            `}
                        </style>
                        {isFetchingMore ? (
                            <div className="d-flex justify-content-center align-items-center flex-grow-1" style={{ minHeight: '44px', paddingBottom: '0.5rem' }}>
                                <Spinner animation="border" variant="danger" />
                            </div>
                        ) : (
                            <div
                                className="d-flex gap-3 align-items-center flex-grow-1 elegant-scrollbar"
                                style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '0.5rem' }}
                            >
                                {referenciasDuplicadas.map((ref, i) => (
                                    <div
                                        key={i}
                                        className={`d-flex align-items-center gap-2 px-3 py-2 rounded-pill cursor-pointer flex-shrink-0 ${activeRef === ref.vReferencia ? 'border border-danger' : 'border border-200'}`}
                                        style={{
                                            backgroundColor: '#fff',
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={() => {
                                            setActiveRef(ref.vReferencia);
                                            setShowOffcanvas(true);
                                        }}
                                    >
                                        <span className={`fw-semi-bold ${activeRef === ref.vReferencia ? 'text-danger' : 'text-700'}`}>
                                            Ref: {ref.vReferencia}
                                        </span>
                                        <span 
                                            className={`badge rounded-pill ${activeRef === ref.vReferencia ? 'bg-danger text-white' : 'bg-200 text-dark'}`}
                                        >
                                            {ref.cantRef}
                                        </span>
                                        {ref.cantCancelados > 0 && (
                                            <span className="badge rounded-pill bg-dark ms-1" title={`${ref.cantCancelados} pagos cancelados`}>
                                                <FontAwesomeIcon icon="ban" /> {ref.cantCancelados}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0" style={{ paddingBottom: '0.5rem' }}>
                                <span className="fs--2 text-500 fw-semi-bold me-1">
                                    Pág {pageNumber} de {totalPages}
                                </span>
                                <button 
                                    className="btn btn-sm btn-outline-secondary px-2 border-200 text-600 bg-white"
                                    disabled={pageNumber === 1}
                                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                                >
                                    <FontAwesomeIcon icon="chevron-left" />
                                </button>
                                <button 
                                    className="btn btn-sm btn-outline-secondary px-2 border-200 text-600 bg-white"
                                    disabled={pageNumber >= totalPages}
                                    onClick={() => setPageNumber(prev => Math.min(totalPages, prev + 1))}
                                >
                                    <FontAwesomeIcon icon="chevron-right" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <OffcanvasPagosDuplicados
                show={showOffcanvas}
                onHide={() => setShowOffcanvas(false)}
                referencia={activeRef}
                token={token}
                setShowModalConciliar={setShowModalConciliar}
                setID_Pago={setID_Pago}
                refreshTrigger={refreshTrigger}
            />
        </Card>
    );
};

PagosReferenciasDuplicadas.propTypes = {
    filters: PropTypes.object,
    setID_Pago: PropTypes.func.isRequired,
    setShowModalConciliar: PropTypes.func.isRequired,
    refreshTrigger: PropTypes.number
};

export default PagosReferenciasDuplicadas;
